import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import { SalesList, NewSale } from "@/pages/Sales";
import Payments from "@/pages/Payments";
import Products from "@/pages/Products";
import { AccountsPage } from "@/pages/Accounts";
import { TransactionsPage } from "@/pages/Transactions";
import Suppliers from "@/pages/Suppliers";
import Upcoming from "@/pages/Upcoming";
import CashFlow from "@/pages/CashFlow";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import CustomerStatement from "@/pages/CustomerStatement";
import IronCalc from "@/pages/IronCalc";
import Quotes from "@/pages/Quotes";
import QuoteForm from "@/pages/QuoteForm";
import QuotePrint from "@/pages/QuotePrint";

const Protected = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Yükleniyor...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

// Auth-protected but WITHOUT the app chrome (sidebar/topbar) — used for print/PDF pages.
const PrintView = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Yükleniyor...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <div className="dark">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/satislar" element={<Protected><SalesList /></Protected>} />
            <Route path="/satislar/yeni" element={<Protected><NewSale /></Protected>} />
            <Route path="/satislar/:id/duzenle" element={<Protected><NewSale /></Protected>} />
            <Route path="/teklifler" element={<Protected><Quotes /></Protected>} />
            <Route path="/teklifler/yeni" element={<Protected><QuoteForm /></Protected>} />
            <Route path="/teklifler/:id" element={<Protected><QuoteForm /></Protected>} />
            <Route path="/teklifler/:id/yazdir" element={<PrintView><QuotePrint /></PrintView>} />
            <Route path="/musteriler" element={<Protected><Customers /></Protected>} />
            <Route path="/musteriler/:id" element={<Protected><CustomerDetail /></Protected>} />
            <Route path="/tedarikciler" element={<Protected><Suppliers /></Protected>} />
            <Route path="/tahsilatlar" element={<Protected><Payments /></Protected>} />
            <Route path="/urunler" element={<Protected><Products /></Protected>} />
            <Route path="/hesaplama" element={<Protected><IronCalc /></Protected>} />
            <Route path="/bankalar" element={<Protected><AccountsPage kind="bank" /></Protected>} />
            <Route path="/kasalar" element={<Protected><AccountsPage kind="cash" /></Protected>} />
            <Route path="/gelirler" element={<Protected><TransactionsPage kind="income" /></Protected>} />
            <Route path="/giderler" element={<Protected><TransactionsPage kind="expense" /></Protected>} />
            <Route path="/yaklasan" element={<Protected><Upcoming /></Protected>} />
            <Route path="/nakit-akisi" element={<Protected><CashFlow /></Protected>} />
            <Route path="/raporlar" element={<Protected><Reports /></Protected>} />
            <Route path="/ayarlar" element={<Protected><Settings /></Protected>} />
            <Route path="/yonetim" element={<Protected><Admin /></Protected>} />
            <Route path="/musteriler/:id/ekstre" element={<PrintView><CustomerStatement /></PrintView>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
